import { useState } from "react";
import { CheckCircle2, Upload, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

const COUNTRIES = ["Oman", "UAE", "Saudi Arabia", "Qatar", "Kuwait", "Bahrain", "India", "United States", "United Kingdom", "Other"];
const BUSINESS_TYPES = ["Logistics & transport", "Retail & e-commerce", "Government & public sector", "Banking & finance", "Healthcare", "Real estate & facilities", "Education", "Manufacturing & distribution", "Automotive", "Other"];
const HELP_WITH = ["Build a new system", "Replace or rebuild an existing one", "Add AI to what we already have", "Enterprise GRC", "Cyber security GRC", "PDPL compliance", "Take over support of our system", "Marketing and lead generation", "Something else"];
const ROLES = ["Owner/Partner", "Managing Director/CEO", "IT Manager/CIO", "Operations Manager", "Compliance/Risk Officer", "Other"];
const BUDGETS = ["Under OMR 5,000", "OMR 5,000–15,000", "OMR 15,000–50,000", "Over OMR 50,000", "Not sure"];
const TIMELINES = ["Urgent", "1–3 months", "3–6 months", "Just exploring"];

const REQUIRED = ["first_name", "last_name", "email", "phone", "company", "country", "business_type", "help_with", "role"];

const inputCls = "h-11 w-full rounded-md border border-wafa-deep/15 bg-white px-3 text-sm text-wafa-ink placeholder:text-wafa-ink/40 focus:outline-none focus:ring-2 focus:ring-wafa-primary focus:ring-offset-1 focus:border-wafa-primary transition-colors";
const selectCls = inputCls + " appearance-none pe-9 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%230E4F3A%22 stroke-width=%222%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-no-repeat bg-[right_10px_center]";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-wafa-deep";
const requiredMark = <span className="ms-0.5 text-wafa-carrot">*</span>;

export function ContactForm() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", company: "",
    country: "", business_type: "", help_with: "", role: "",
    budget: "", timeline: "", message: "", file_name: "",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    const missing = REQUIRED.filter((k) => !form[k]?.trim());
    if (missing.length) {
      setStatus({ state: "error", error: `Please complete: ${missing.join(", ")}` });
      return;
    }
    setStatus({ state: "submitting", error: "" });
    try {
      await api.post("/contact", form);
      setStatus({ state: "success", error: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Something went wrong.";
      setStatus({ state: "error", error: typeof msg === "string" ? msg : "Submission failed." });
    }
  };

  if (status.state === "success") {
    return (
      <section data-testid="contact-form-section" className="bg-white">
        <div className="mx-auto max-w-3xl px-4 py-20 lg:px-6 lg:py-28">
          <div className="rounded-xl border border-wafa-primary/30 bg-wafa-cream/40 p-10 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-wafa-primary" />
            <h3 className="mt-4 font-heading text-2xl font-semibold text-wafa-deep">Thank you — we have you.</h3>
            <p className="mt-3 text-sm text-wafa-ink/75">
              An engineer will reach out within one business day to set up your thirty-minute scoping call.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="contact-form-section" className="bg-white" id="contact">
      <div className="mx-auto max-w-4xl px-4 py-20 lg:px-6 lg:py-28">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary">Contact</div>
          <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
            Book your free scoping call.
          </h2>
          <p className="mt-4 text-base text-wafa-ink/75 lg:text-lg">
            Tell us a little about your business. An engineer — not a sales team — will get in touch within one business day.
          </p>
        </div>

        <form onSubmit={onSubmit} data-testid="contact-form" className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className={labelCls} htmlFor="first_name">First name{requiredMark}</label>
            <input id="first_name" data-testid="contact-first-name" className={inputCls + " mt-2"} value={form.first_name} onChange={set("first_name")} />
          </div>
          <div>
            <label className={labelCls} htmlFor="last_name">Last name{requiredMark}</label>
            <input id="last_name" data-testid="contact-last-name" className={inputCls + " mt-2"} value={form.last_name} onChange={set("last_name")} />
          </div>
          <div>
            <label className={labelCls} htmlFor="email">Email{requiredMark}</label>
            <input id="email" type="email" data-testid="contact-email" className={inputCls + " mt-2"} value={form.email} onChange={set("email")} />
          </div>
          <div>
            <label className={labelCls} htmlFor="phone">Phone{requiredMark}</label>
            <input id="phone" data-testid="contact-phone" className={inputCls + " mt-2"} value={form.phone} onChange={set("phone")} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="company">Company{requiredMark}</label>
            <input id="company" data-testid="contact-company" className={inputCls + " mt-2"} value={form.company} onChange={set("company")} />
          </div>
          <div>
            <label className={labelCls} htmlFor="country">Country{requiredMark}</label>
            <select id="country" data-testid="contact-country" className={selectCls + " mt-2"} value={form.country} onChange={set("country")}>
              <option value="">Select country</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="business_type">Business type{requiredMark}</label>
            <select id="business_type" data-testid="contact-business-type" className={selectCls + " mt-2"} value={form.business_type} onChange={set("business_type")}>
              <option value="">Select business type</option>
              {BUSINESS_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="help_with">What can we help you with?{requiredMark}</label>
            <select id="help_with" data-testid="contact-help-with" className={selectCls + " mt-2"} value={form.help_with} onChange={set("help_with")}>
              <option value="">Select an option</option>
              {HELP_WITH.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="role">Your role{requiredMark}</label>
            <select id="role" data-testid="contact-role" className={selectCls + " mt-2"} value={form.role} onChange={set("role")}>
              <option value="">Select role</option>
              {ROLES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="budget">Budget band</label>
            <select id="budget" data-testid="contact-budget" className={selectCls + " mt-2"} value={form.budget} onChange={set("budget")}>
              <option value="">Select budget</option>
              {BUDGETS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="timeline">Timeline</label>
            <select id="timeline" data-testid="contact-timeline" className={selectCls + " mt-2"} value={form.timeline} onChange={set("timeline")}>
              <option value="">Select timeline</option>
              {TIMELINES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="message">Message</label>
            <textarea id="message" rows={5} data-testid="contact-message" className={"mt-2 w-full rounded-md border border-wafa-deep/15 bg-white p-3 text-sm text-wafa-ink placeholder:text-wafa-ink/40 focus:outline-none focus:ring-2 focus:ring-wafa-primary focus:border-wafa-primary transition-colors"} value={form.message} onChange={set("message")} placeholder="Anything else we should know?" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Attachment (optional)</label>
            <div className="mt-2 flex items-center gap-3">
              <input
                type="file"
                id="file"
                data-testid="contact-file"
                onChange={(e) => setForm({ ...form, file_name: e.target.files?.[0]?.name || "" })}
                className="hidden"
              />
              <label htmlFor="file" className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-wafa-deep/15 bg-white px-4 py-2 text-sm font-medium text-wafa-deep hover:border-wafa-deep">
                <Upload className="h-4 w-4" />
                Choose file
              </label>
              {form.file_name ? <span className="text-xs text-wafa-ink/70">{form.file_name}</span> : null}
            </div>
          </div>

          {status.state === "error" ? (
            <div className="sm:col-span-2 flex items-center gap-2 rounded-md border border-wafa-tomato/30 bg-wafa-tomato/5 px-3 py-2 text-sm text-wafa-tomato">
              <AlertCircle className="h-4 w-4" />
              <span data-testid="contact-error">{status.error}</span>
            </div>
          ) : null}

          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={status.state === "submitting"}
              data-testid="contact-submit"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-wafa-primary px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-wafa-deep disabled:opacity-60"
            >
              {status.state === "submitting" ? "Sending…" : "Book My Free Scoping Call"}
            </button>
            <p className="mt-3 text-xs text-wafa-ink/60">
              By submitting you agree to our <a href="/privacy-policy" className="underline">Privacy Policy</a>. We reply within one business day.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
